import { useCallback, useEffect, useRef, type MutableRefObject } from 'react';

import type { JWPlayer } from '../../../types/jwplayer';

/** JW Player 232*** — media / HLS manifest not yet available (still processing). */
export const JW_MEDIA_UNAVAILABLE_ERROR_PREFIX = '232';

export const MAX_MEDIA_RETRY_ATTEMPTS = 3;
export const MEDIA_RETRY_DELAY_MS = 3000;

export const isMediaUnavailableError = (code?: number | null): boolean =>
  typeof code === 'number' && String(code).startsWith(JW_MEDIA_UNAVAILABLE_ERROR_PREFIX);

type UseJwPlayerMediaRetryParams = {
  playerRef: MutableRefObject<JWPlayer | undefined>;
  /** Reload the current media (e.g. player.load / setup). */
  onRetry: () => void;
  /** Called after the third retry still fails with a 232*** (non-live) error. */
  onMaxRetriesReached?: () => void;
  /**
   * Live streams: show final error UI instead of tearing down.
   * Called after max live retries with the last error payload.
   */
  showPlaybackError?: (event: { code?: number; message?: string }) => void;
  /** Toggle the single "Connecting to server..." label during live retries. */
  setReconnecting?: (value: boolean) => void;
};

/**
 * Automatic retry for:
 * - JW Player 232*** (any content) — media not yet available
 * - Any error on a valid live stream — with connecting label + showPlaybackError on exhaustion
 *
 * Up to 3 attempts, 3s apart (3s / 6s / 9s); cancels on success; single timer; cleans up on unmount.
 */
export function useJwPlayerMediaRetry({
  playerRef,
  onRetry,
  onMaxRetriesReached,
  showPlaybackError,
  setReconnecting,
}: UseJwPlayerMediaRetryParams) {
  const retryAttemptRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastErrorRef = useRef<{ code?: number; message?: string } | null>(null);
  const onRetryRef = useRef(onRetry);
  const onMaxRetriesReachedRef = useRef(onMaxRetriesReached);
  const showPlaybackErrorRef = useRef(showPlaybackError);
  const setReconnectingRef = useRef(setReconnecting);

  onRetryRef.current = onRetry;
  onMaxRetriesReachedRef.current = onMaxRetriesReached;
  showPlaybackErrorRef.current = showPlaybackError;
  setReconnectingRef.current = setReconnecting;

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current == null) return;
    try {
      clearTimeout(retryTimerRef.current);
    } catch {
      /* timer may already be cleared */
    }
    retryTimerRef.current = null;
  }, []);

  const removeConnectingLabel = useCallback(() => {
    try {
      setReconnectingRef.current?.(false);
    } catch {
      /* label target may be gone */
    }
  }, []);

  const cancelRetries = useCallback(
    (reason: 'success' | 'reset' | 'unmount' = 'reset') => {
      const hadPendingOrAttempts = retryTimerRef.current !== null || retryAttemptRef.current > 0;

      clearRetryTimer();
      retryAttemptRef.current = 0;
      lastErrorRef.current = null;
      removeConnectingLabel();

      if (reason === 'success' && hadPendingOrAttempts) {
        console.log('Video loaded successfully. Cancelling retries.');
      }
    },
    [clearRetryTimer, removeConnectingLabel],
  );

  const scheduleRetry = useCallback(
    (
      errorCode?: number | null,
      options?: { isLiveStream?: boolean; message?: string },
    ): boolean => {
      const isLive = !!options?.isLiveStream;
      const is232 = isMediaUnavailableError(errorCode);

      // Non-live / invalid: only 232*** is eligible; everything else shows error immediately
      if (!isLive && !is232) {
        return false;
      }

      // Prevent multiple retry timers / duplicate connecting labels
      if (retryTimerRef.current !== null) {
        return true;
      }

      if (retryAttemptRef.current >= MAX_MEDIA_RETRY_ATTEMPTS) {
        console.log('Maximum retry attempts reached.');
        clearRetryTimer();
        const lastError = lastErrorRef.current ?? { code: errorCode ?? undefined, message: options?.message };
        retryAttemptRef.current = 0;
        lastErrorRef.current = null;
        removeConnectingLabel();

        if (isLive) {
          showPlaybackErrorRef.current?.(lastError);
        } else {
          onMaxRetriesReachedRef.current?.();
        }
        return false;
      }

      const nextAttempt = retryAttemptRef.current + 1;
      retryAttemptRef.current = nextAttempt;
      lastErrorRef.current = { code: errorCode ?? undefined, message: options?.message };

      if (isLive) {
        try {
          setReconnectingRef.current?.(true);
        } catch {
          /* ignore */
        }
      }

      if (nextAttempt === 1) {
        console.log(`Retry ${nextAttempt}/${MAX_MEDIA_RETRY_ATTEMPTS} after 3 seconds...`);
      } else {
        console.log(`Retry ${nextAttempt}/${MAX_MEDIA_RETRY_ATTEMPTS}...`);
      }

      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;

        if (!playerRef.current) {
          return;
        }

        try {
          onRetryRef.current();
        } catch {
          /* player may have been removed */
        }
      }, MEDIA_RETRY_DELAY_MS);

      return true;
    },
    [clearRetryTimer, playerRef, removeConnectingLabel],
  );

  // Clean up all timers when the React component unmounts
  useEffect(() => {
    return () => {
      cancelRetries('unmount');
    };
  }, [cancelRetries]);

  return {
    scheduleRetry,
    cancelRetries,
  };
}
